import { useRef } from 'react'
import { InfoIcon } from '@/shared/ui/icons/InfoIcon'
import { CityIcon } from '@/shared/ui/icons/CityIcon'
import type { OrgNewDraft } from '@/public/features/org/types'
import { INTRO_MAX } from '@/public/features/org/constants/orgNew'
import { StepHeadLarge } from '@/public/features/org/ui/orgNew/parts'

export function StepIntro({ draft }: { draft: OrgNewDraft }) {
  const file = useRef<HTMLInputElement>(null)
  const { logo } = draft

  return (
    <section className="flex flex-col gap-8">
      <StepHeadLarge
        title="어떤 기업인지 소개해 주세요."
        desc="기업의 주요 활동과 서비스를 간단히 알려주세요."
      />

      <div className="flex flex-col gap-2">
        <span className="flex items-start gap-1 text-body-sm font-normal text-black-600">로고/아이콘</span>
        <div className="flex items-center gap-3">
          <div
            className={
              logo === null
                ? 'relative flex h-[60px] w-[60px] shrink-0 items-center justify-center overflow-hidden rounded-[99px] border border-black/5 bg-violet-100'
                : 'relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-[99px] border border-black/5 bg-violet-100'
            }
            style={{ background: draft.logoTone }}
          >
            {logo === null ? (
              <span className="flex items-center justify-center text-black-100 opacity-25" aria-hidden>
                <CityIcon size={36} />
              </span>
            ) : (
              <>
                <img className="block h-full w-full object-cover" src={logo.dataUrl} alt="올린 로고" />
                <button
                  type="button"
                  className="ui-pressable absolute top-[-1px] left-[-1px] h-6 w-6 cursor-pointer rounded-[99px] border-0 bg-black-500 p-0 text-body-md leading-none text-white-50"
                  onClick={() => draft.setLogo(null)}
                  aria-label="로고 지우기"
                >
                  ×
                </button>
              </>
            )}
          </div>

          <div className="flex flex-col items-start justify-center gap-1.5">
            <button
              type="button"
              className="ui-pressable inline-flex cursor-pointer items-center justify-center gap-1.5 border border-white-400 bg-white-200 px-3 py-2.5 font-base text-body-md whitespace-nowrap text-black-600 h-10 rounded-xl disabled:cursor-not-allowed disabled:border-white-300 disabled:text-white-500"
              onClick={() => file.current?.click()}
            >
              {logo === null ? '이미지 올리기' : '변경하기'}
            </button>
            <p className="m-0 text-body-sm-reading font-medium text-black-100">
              이미지는 2MB이하로 업로드 가능합니다.
            </p>
          </div>

          <input
            ref={file}
            type="file"
            className="hidden"
            accept="image/*"
            onChange={(e) => {
              draft.setLogo(e.target.files?.[0] ?? null)
              // 같은 파일을 다시 고를 수 있게 값을 비운다(브라우저가 같은 값이면 change 를 안 낸다).
              e.target.value = ''
            }}
          />
        </div>
        {draft.logoError !== null && (
          <p className="m-0 flex items-center gap-1 text-label font-medium [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0 text-[#f54550]" role="alert">
            <InfoIcon />
            <span>{draft.logoError}</span>
          </p>
        )}
      </div>

      <label className="flex flex-col gap-1.5">
        <span className="flex items-start gap-1 text-caption font-medium text-black-800">
          기업 소개
          <span className="mt-0.5 h-1 w-1 shrink-0 rounded-full bg-violet-500" aria-hidden />
        </span>
        <textarea
          className="h-[342px] w-full resize-none rounded-lg border border-white-400 bg-white-50 px-3.5 py-3.5 font-[inherit] text-body-md text-black-800 placeholder:text-black-100"
          value={draft.intro}
          onChange={(e) => draft.setIntro(e.target.value)}
          maxLength={INTRO_MAX}
          placeholder="어떤 활동과 서비스를 제공하는 기업인지 소개해 주세요."
        />
      </label>
      <p className="m-0 flex items-baseline justify-end gap-0.5 text-body-sm font-medium whitespace-nowrap">
        <span className="leading-[1.6] text-violet-900">{draft.intro.length}</span>
        <span className="leading-[1.32] text-[#989898]">{`/${INTRO_MAX}`}</span>
      </p>

      <IntroTextarea
        label="기업 미션"
        placeholder="기업 미션을 작성해주세요."
        value={draft.mission}
        onChange={draft.setMission}
      />
      <IntroTextarea
        label="기업 비전"
        placeholder="기업 비전을 작성해주세요."
        value={draft.vision}
        onChange={draft.setVision}
      />
    </section>
  )
}
function IntroTextarea({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string
  placeholder: string
  value: string
  onChange: (v: string) => void
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-body-md font-normal text-black-600">{label}</span>
      <textarea
        className="h-[180px] w-full resize-none rounded-lg border border-white-400 bg-white-50 px-3.5 py-3.5 font-[inherit] text-body-md text-black-800 placeholder:text-black-100"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={INTRO_MAX}
        placeholder={placeholder}
      />
    </label>
  )
}
